/*
 * Developed for G.J. Gardner Homes by Softeq Development Corporation
 * http://www.softeq.com
 */

import {
  ChangeDetectionStrategy,
  Component,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  SimpleChanges,
} from '@angular/core';
import { GraphicalTakeOffPoint, GtoUom } from '@gh/builder-shared-data';
import { ID } from '@gh/common-shared-data';
import { isNotNil } from '@gh/core-util';
import { isNil } from 'lodash';
import { Item, Path, Point, Shape } from 'paper';
import {
  getGraphicalTakeOffColor,
  getGraphicalTakeOffColorPalette,
  getGraphicalTakeOffMeasurementId, getGraphicalTakeOffTopUom,
  getGraphicalTakeOffTopViewId,
  GraphicalTakeOffMeasurement,
  GraphicalTakeOffTopView,
  GraphicalTakeOffViewTransform,
  isGraphicalTakeOffMeasurementClosed,
} from '../../resources/models';
import {
  composePaperEffects,
  constantScalePaperEffect,
  constantScaleStrokeWidthPaperEffect,
  createFilling,
  GTO_MEASUREMENT_STROKE_WIDTH, moveOnFront, moveOnBack,
  toPaperPoint,
  visiblePaperEffect,
} from './canvas.utils';
import {
  GraphicalTakeOffElementBase,
  paperEffect,
  PaperElementEffect,
} from './graphical-take-off-element';

const setFilling = (path: Item, item?: Item): void => {
  path['_filling_'] = item;
};
const getFilling = (path: Item): Maybe<Item> => path['_filling_'];
const removeFilling = (path: Item) => {
  const filling = getFilling(path);
  if (filling) {
    filling.remove();
  }
  setFilling(path, void 0);
};

const setFirstPoint = (path: Item, item?: Item): void => {
  path['_draftPoint_'] = item;
};
const getFirstPoint = (path: Item): Maybe<Item> => path['_draftPoint_'];
const setFirstPointPosition = (path: Item, point: Point, scale: number) => {
  const firstPoint = getFirstPoint(path);
  if (firstPoint) {
    firstPoint.position = point;
    firstPoint['radius'] = 5 * scale;
    firstPoint['strokeWidth'] = scale;
  } else {
    const circle = Shape.Circle(point, 5 * scale);
    circle.strokeColor = path.strokeColor;
    circle.fillColor = 'white';
    circle['strokeWidth'] = scale;
    setFirstPoint(path, circle);
  }
};
const removeFirstPoint = (path: Item) => {
  const firstPoint = getFirstPoint(path);
  if (firstPoint) {
    firstPoint.remove();
    setFirstPoint(path);
  }
};

const measurementPaperEffects: PaperElementEffect[] = [
  paperEffect('visible', visiblePaperEffect),
  paperEffect(['selected'], (item: Item, [selected]: [boolean]) => {
    item.selected = selected;
    const filling = getFilling(item);
    if (selected) {
      moveOnFront(item, 'measurement');
      if (filling) {
        moveOnFront(filling, 'filling');
      }
    } else {
      moveOnBack(item, 'measurement');
      if (filling) {
        moveOnBack(filling, 'filling');
      }
    }
  }),
  paperEffect('viewTransform', composePaperEffects(constantScalePaperEffect, constantScaleStrokeWidthPaperEffect)),
  paperEffect(['top', 'measurement', 'viewTransform'], (path: Path,
                                                        [top, measurement, viewTransform],
                                                        [previousTop, previousMeasurement, previousViewTransform]) => {
    const { points: nextPoints } = measurement;
    const previousPoints = previousMeasurement ? previousMeasurement.points : [];
    const previousMeasurementDraftMode = previousMeasurement ? previousMeasurement.draftMode : false;

    if (measurement.draftMode === previousMeasurementDraftMode
      && nextPoints === previousPoints
      && viewTransform === previousViewTransform) {
      return;
    }

    path.removeSegments();
    removeFilling(path);

    let nextTransformedPoints: GraphicalTakeOffPoint[];

    if (nextPoints.length === 1) {
      nextTransformedPoints = [nextPoints[0]];
      setFirstPointPosition(path, toPaperPoint(nextTransformedPoints[0]), viewTransform.scale);
    } else {
      removeFirstPoint(path);

      nextTransformedPoints = nextPoints.map((point: GraphicalTakeOffPoint) => point);
      nextTransformedPoints.forEach((point) => path.add(toPaperPoint(point)));

    }

    const closed = isGraphicalTakeOffMeasurementClosed(measurement);
    path.closed = closed;

    // do not draw filling in draft mode
    if (closed && !measurement.draftMode && isNil(getFilling(path))) {
      const filling = createFilling(top, nextTransformedPoints);
      moveOnBack(filling, 'filling');
      setFilling(path, filling);
    } else if ((!closed || measurement.draftMode) && isNotNil(getFilling(path))) {
      removeFilling(path);
    }
  }),
];

@Component({
  standalone: false,
  selector: 'gh-graphical-take-off-canvas-length-measurement',
  templateUrl: 'graphical-take-off-canvas-length-measurement.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class GraphicalTakeOffCanvasLengthMeasurementComponent extends GraphicalTakeOffElementBase implements OnInit, OnDestroy, OnChanges {
  @Input() top: GraphicalTakeOffTopView;
  @Input() measurement: GraphicalTakeOffMeasurement;
  @Input() visible: boolean;
  @Input() viewTransform: GraphicalTakeOffViewTransform;
  @Input() dataBoxVisible: boolean;
  @Input() highlightedDataBoxId?: ID;
  @Input() selected: boolean;
  @Input() withDash: boolean;

  uom: GtoUom;
  isMeasurementClosed: boolean;

  constructor() {
    super(measurementPaperEffects);
  }

  ngOnInit(): void {
    super.ngOnInit();
    this.uom = getGraphicalTakeOffTopUom(this.top);
  }

  ngOnChanges(changes: SimpleChanges): void {
    super.ngOnChanges(changes);

    const measurement = changes['measurement'];
    if (measurement) {
      this.isMeasurementClosed = isGraphicalTakeOffMeasurementClosed(measurement.currentValue);
    }
  }

  ngOnDestroy(): void {
    removeFirstPoint(this.item);
    removeFilling(this.item);
    super.ngOnDestroy();
  }

  protected createPaperItem(): Item {
    const { measurement, top } = this;
    const { points } = measurement;
    const color = getGraphicalTakeOffColor(top);
    const palette = getGraphicalTakeOffColorPalette(color);
    const path = new Path(points.map(toPaperPoint));
    path.strokeColor = palette.primary;
    path.strokeWidth = GTO_MEASUREMENT_STROKE_WIDTH;
    if (this.withDash) {
      path.strokeCap = 'round';
      path.dashArray = [10, 12];
    }
    path.closed = isGraphicalTakeOffMeasurementClosed(measurement);
    path.data = {
      topId: getGraphicalTakeOffTopViewId(this.top),
      measurementId: getGraphicalTakeOffMeasurementId(this.measurement),
      type: 'measurement',
    };

    moveOnBack(path, 'measurement');

    return path;
  }
}
